import type { MailboxInfo } from "owa-client-types";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { getIsCopilotUIDisabled } from "owa-mail-copilot-settings/lib/selectors/getIsCopilotUIDisabled";
import { isMailboxSharedOrDelegateUserMailbox } from "owa-account-shared-mailbox-utils";
import hasBizChatLicense from "./hasBizChatLicense";
import hasM365CopilotLicense from "./hasM365CopilotLicense";

/**
 * Indicates whether the Inbox Rules Copilot feature should be enabled for the given mailbox.
 *
 * @param mailboxInfo The mailbox being checked (not the global/primary mailbox)
 * @returns {boolean} True when the Copilot inbox rules feature is eligible (all internal prerequisites
 * are satisfied); false to hide the Copilot inbox rules feature.
 */
export const isInboxRulesCopilotEnabled = (mailboxInfo: MailboxInfo): boolean => {
    const isAIEnabled = isGlobalFeatureEnabled("mon-copilot-bizchat") &&
        (hasBizChatLicense(mailboxInfo) || hasM365CopilotLicense(mailboxInfo));
    const isSharedOrDelegateMailbox = isMailboxSharedOrDelegateUserMailbox(mailboxInfo);
    
    return (
    // 'settings-rules-bizchat': governing exposure flag for the Inbox Rules Copilot experience
    // (turning this flight/flag on is what actually lights up the feature to end users)
    isGlobalFeatureEnabled("settings-rules-bizchat") &&
        // Check that BizChat is enabled and user has proper AI SKU for this specific mailbox
        isAIEnabled &&
        // Do not show for shared or delegate mailboxes (similar to mail search)
        !isSharedOrDelegateMailbox &&
        // Do not show for archive mailboxes
        !(mailboxInfo.type === "ArchiveMailbox") &&
        // Do not show Copilot if all Copilot UI has been disabled for this mailbox
        !getIsCopilotUIDisabled(mailboxInfo));
};
